import { useMemo, useState } from "react";

import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

import PropTypes from "prop-types";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { useMaterialUIController } from "context";

import { useNavigate } from "react-router-dom";

function Sprayers({ sprayers }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const navigate = useNavigate();

  const filteredSprayers = useMemo(
    () =>
      sprayers.filter((sprayer) =>
        sprayer.sprayerName.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [sprayers, searchQuery]
  );

  const pagedSprayers = filteredSprayers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6" fontWeight="medium">
          Sprayer Information
        </MDTypography>
        <TextField
          id="search-sprayers"
          label="Search Sprayers"
          size="medium"
          sx={{
            minWidth: { xs: "100%", sm: 360 },
            "& .MuiInputBase-root": {
              backgroundColor: "rgba(76, 175, 80, 0.08)",
            },
            "& .MuiInputLabel-root": {
              fontSize: "1.2rem",
            },
          }}
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
            setPage(0);
          }}
        />
      </MDBox>
      <MDButton
        variant="contained"
        color="success"
        onClick={() => {
          navigate("/setup/addsprayer/");
        }}
      >
        <Icon>add</Icon>&nbsp;Add Sprayer
      </MDButton>
      <MDBox mt={1}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead sx={{ display: "table-header-group", padding: 0, borderRadius: 0 }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Calibration Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedSprayers.map((sprayer) => (
                <TableRow key={sprayer.key} hover>
                  <TableCell>{sprayer.sprayerName}</TableCell>
                  <TableCell>{sprayer.sprayerCode}</TableCell>
                  <TableCell>{`${sprayer.tankCapacity}L`}</TableCell>
                  <TableCell>{sprayer.active}</TableCell>
                  <TableCell>{sprayer.calibrationIssued}</TableCell>
                  <TableCell align="right">
                    <MDButton
                      variant="text"
                      color={darkMode ? "white" : "dark"}
                      onClick={() => {
                        navigate(`/setup/sprayer/${sprayer.key}`, {
                          state: sprayer,
                        });
                      }}
                    >
                      <Icon>edit</Icon>&nbsp;edit
                    </MDButton>
                  </TableCell>
                </TableRow>
              ))}
              {pagedSprayers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No sprayers found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredSprayers.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </MDBox>
    </MDBox>
  );
}

Sprayers.propTypes = {
  sprayers: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
};

export default Sprayers;
